import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { useDocumentTitle } from '../../hooks';
import EngagementGanttChart from '../PersonSearch/EngagementGanttChart';
import WorkloadChart from '../PersonSearch/WorkloadChart';
import { companyCountHistory, datedPeriods } from './addressHistory';

export interface Address { addressId: string; street: string; postalCode: string; postalAddress: string }
export interface AddressPeriod { organizationNumber: string; organizationName: string; startDate: string | null; endDate: string | null }
interface Page<T> { items: T[]; hasMore: boolean }
const addressName = (address: Address) => `${address.street}, ${address.postalCode} ${address.postalAddress}`;

export default function AddressSearch() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  useDocumentTitle(`${t('addressSearch.title')} — Breadify`);
  const [query, setQuery] = useState('');
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selected, setSelected] = useState<Address | null>(null);
  const [periods, setPeriods] = useState<AddressPeriod[]>([]);
  const [searchMore, setSearchMore] = useState(false);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const request = useRef(0);
  const history = useMemo(() => companyCountHistory(periods), [periods]);
  const timeline = useMemo(() => datedPeriods(periods).map(period => ({ ...period, functionText: t('addressSearch.registered') })), [periods, t]);
  const load = useCallback(async (address: Address) => {
    const id = ++request.current;
    setSelected(address); setLoading(true); setError(false); setPeriods([]);
    try {
      const all: AddressPeriod[] = [];
      let more = true;
      while (more) {
        const data = await api.get<Page<AddressPeriod>>(`/v1/addresses/${address.addressId}/companies?offset=${all.length}`);
        if (id !== request.current) return;
        // A non-advancing page cannot produce complete history; offer retry instead.
        if (data.hasMore && !data.items.length) throw new Error('Empty history page');
        all.push(...data.items);
        more = data.hasMore;
      }
      setPeriods(all);
    } catch { if (id === request.current) setError(true); }
    finally { if (id === request.current) setLoading(false); }
  }, []);
  const executeSearch = useCallback(async (value: string, addressId?: string | null) => {
    if (value.trim().length < 3) return;
    const id = ++request.current;
    setLoading(true); setError(false); setSelected(null); setPeriods([]); setAddresses([]); setSearched(false);
    try {
      const data = await api.get<Page<Address>>(`/v1/addresses?query=${encodeURIComponent(value.trim())}`);
      if (id !== request.current) return;
      setAddresses(data.items); setSearchMore(data.hasMore); setSearched(true);
      const match = addressId && data.items.find(address => address.addressId === addressId);
      if (match) await load(match);
    } catch { if (id === request.current) setError(true); }
    finally { if (id === request.current) setLoading(false); }
  }, [load]);
  useEffect(() => {
    const value = searchParams.get('query');
    if (value) {
      setQuery(value);
      void executeSearch(value, searchParams.get('addressId'));
    }
  }, [executeSearch, searchParams]);
  const search = (event: React.FormEvent) => {
    event.preventDefault();
    void executeSearch(query);
  };
  return <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', overflowY: 'auto' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mb: 'var(--spacing-xs)', gap: 'var(--spacing-md)', flexShrink: 0, py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <LocationOnIcon sx={{ fontSize: 28, mr: 'var(--spacing-sm)', color: 'primary.main' }} />
        <Typography variant="h6" component="h1" sx={{ fontWeight: 'bold' }}>{t('addressSearch.title')}</Typography>
      </Box>
      <Box component="form" onSubmit={search} sx={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center', ml: { sm: 'var(--spacing-lg)' }, maxWidth: '100%' }}>
        <TextField label={t('addressSearch.input')} placeholder={t('addressSearch.placeholder')} value={query} onChange={e => setQuery(e.target.value)} size="small" variant="outlined"
          sx={{ width: 280, maxWidth: '100%', '& .MuiInputBase-input': { padding: '6px 14px', fontSize: '0.875rem' } }} InputLabelProps={{ sx: { fontSize: '0.875rem' } }} />
        <Button type="submit" variant="contained" size="small" disabled={loading || query.trim().length < 3} startIcon={loading ? <CircularProgress size={16} color="inherit" aria-label={t('addressSearch.loading')} /> : <LocationOnIcon sx={{ fontSize: 20 }} />}>{t('addressSearch.search')}</Button>
      </Box>
    </Box>
    {!searched && !loading && !error && <Typography color="text.secondary" sx={{ mt: 'var(--spacing-md)' }}>{t('addressSearch.description')}</Typography>}
    {error && <Alert severity="error" sx={{ mb: 'var(--spacing-lg)' }}>{t('addressSearch.error')}{selected && <Button onClick={() => load(selected)}>{t('addressSearch.retry')}</Button>}</Alert>}
    {searched && !addresses.length && <Alert severity="info">{t('addressSearch.noAddresses')}</Alert>}
    {!!addresses.length && <Paper elevation={1} sx={{ mb: 'var(--spacing-md)', flexShrink: 0 }}>
      <Box sx={{ p: 1, px: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}><Typography variant="subtitle1" fontWeight="bold">{t('addressSearch.choose')}</Typography></Box>
      <Box sx={{ p: 1, maxHeight: 180, overflowY: 'auto' }}>
        {addresses.map(address => <Button key={address.addressId} size="small" fullWidth variant={selected?.addressId === address.addressId ? 'contained' : 'text'} disabled={loading} onClick={() => load(address)} sx={{ justifyContent: 'flex-start', textAlign: 'left' }}>{addressName(address)}</Button>)}
      </Box>
      {searchMore && <Alert severity="info">{t('addressSearch.refine')}</Alert>}
    </Paper>}
    {selected && !loading && !error && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)', minHeight: 0 }}>
      <Paper elevation={1} sx={{ width: '100%', display: 'flex', flexDirection: 'column', maxHeight: 400, flexShrink: 0 }}>
        <Box sx={{ p: 1, px: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
          <Typography variant="subtitle1" fontWeight="bold">{addressName(selected)}</Typography>
          <Typography variant="caption" color="text.secondary">{t('addressSearch.periodNote')}</Typography>
        </Box>
        {!periods.length ? <Alert severity="info">{t('addressSearch.noCompanies')}</Alert> : <TableContainer sx={{ flex: 1, overflow: 'auto' }}><Table stickyHeader size="small" aria-label={t('addressSearch.resultsLabel')} sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem', py: 0.5 } }}>
          <TableHead><TableRow>{['company', 'status', 'from', 'until'].map(key => <TableCell key={key} sx={{ fontWeight: 'bold' }}>{t(`addressSearch.${key}`)}</TableCell>)}</TableRow></TableHead>
          <TableBody>{periods.map((period, index) => <TableRow hover key={`${period.organizationNumber}-${period.startDate}-${index}`}>
            <TableCell><Link component={RouterLink} sx={{ fontWeight: 'bold' }} to={`/companyassessment/${period.organizationNumber}`}>{period.organizationName || period.organizationNumber}</Link><Typography sx={{ fontSize: '0.7rem' }} color="text.secondary">{period.organizationNumber}</Typography></TableCell>
            <TableCell><Chip size="small" sx={{ height: 20, fontSize: '0.7rem' }} color={period.endDate ? 'default' : 'success'} label={t(`addressSearch.${period.endDate ? 'historical' : 'current'}`)} /></TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{period.startDate ?? '—'}</TableCell><TableCell sx={{ whiteSpace: 'nowrap' }}>{period.endDate ?? t('personSearch.present')}</TableCell>
          </TableRow>)}</TableBody>
        </Table></TableContainer>}
      </Paper>
      {timeline.length < periods.length && <Alert severity="info">{t('addressSearch.unknownDates')}</Alert>}
      {!!timeline.length && <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 'var(--spacing-md)', alignItems: 'flex-start' }}>
        <Paper elevation={1} sx={{ p: 2, flex: 2, width: { xs: '100%', lg: 'auto' }, minWidth: 0, overflowX: 'auto' }}>
          <Box sx={{ minWidth: 480 }}><EngagementGanttChart data={timeline} title={t('addressSearch.timeline')} activeLabel={t('addressSearch.current')} endedLabel={t('addressSearch.historical')} /></Box>
        </Paper>
        <Paper elevation={1} sx={{ p: 2, flex: 1, minWidth: { lg: 300 }, width: { xs: '100%', lg: 'auto' } }}>
          <WorkloadChart data={[]} series={history} title={t('addressSearch.companyCount')} countLabel={t('addressSearch.companies')} />
        </Paper>
      </Box>}
    </Box>}
  </Box>;
}
